// src/pages/Billing.jsx
// Settings → Billing. Trial and subscription status, the price list for the
// company's market, checkout, and managing an existing subscription.
//
// Pricing is by number of active people (archived and deactivated people
// don't count). Everything is included at every size; annual billing is
// two months free. Payments, card details, invoices and VAT/GST numbers are
// handled by Stripe (checkout and the customer portal).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreditCard, Users, Globe, Sparkles, CalendarClock, ExternalLink, RefreshCw, Check, Tag, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { useApp } from '../context/AppContext';
import {
  cx, errorMessage, Button, Chip, Notice, SectionHeader, SettingRow, Segmented, SkeletonRows,
  inputClass, selectClass, useConfirm,
} from './settingsUI';

const SALES_EMAIL = process.env.REACT_APP_SALES_EMAIL || 'hello@clock-out.app';

const MARKET_OPTIONS = [
  { code: 'GB', name: 'United Kingdom (GBP)' },
  { code: 'AU', name: 'Australia (AUD)' },
  { code: 'ZA', name: 'South Africa (ZAR)' },
];

function fmtDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
}

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

// ─── Status card at the top ──────────────────────────────────────────────────
function StatusCard({ summary }) {
  const { status, access, trial, subscription: sub, enterprise } = summary;
  const reason = access?.reason;

  let tone = 'ink';
  let title;
  let body;

  if (reason === 'override' && access.state === 'full') {
    title = 'Complimentary access';
    body = access.overrideUntil ? `Full access until ${fmtDate(access.overrideUntil)}.` : 'Full access, arranged with the Clock-out team.';
  } else if (status === 'manual') {
    title = 'Billed by invoice';
    body = enterprise?.formatted
      ? `Enterprise plan · ${enterprise.formatted} per ${enterprise.interval === 'year' ? 'year' : 'month'}. Contact us about any changes.`
      : 'Your plan is invoiced directly. Contact us about any changes.';
  } else if (status === 'trialing' && access?.state === 'full') {
    tone = 'trial';
    title = `Free trial · ${plural(trial.daysLeft ?? 0, 'day')} left`;
    body = `Everything is included while you try Clock-out — no card needed. Your trial ends on ${fmtDate(trial.endsAt)}. Choose a plan any time; you won't be charged until the trial ends.`;
  } else if (reason === 'trial_expired') {
    tone = 'bad';
    title = 'Your free trial has ended';
    body = 'Your account is read only — everyone can still sign in and see the calendar, and nothing has been deleted. Choose a plan to start booking leave again.';
  } else if (reason === 'payment_failed') {
    tone = 'bad';
    title = "We couldn't take your last payment";
    body = 'Your account is read only until payment goes through. Update your card in "Manage billing" and we\'ll retry straight away.';
  } else if (reason === 'cancelled') {
    tone = 'bad';
    title = 'Your subscription has ended';
    body = 'Your account is read only and all your data is kept. Resubscribe to pick up where you left off.';
  } else if (sub) {
    const price = sub.tier?.formatted ? `${sub.tier.formatted} per ${sub.interval === 'year' ? 'year' : 'month'}` : null;
    title = enterprise ? 'Enterprise plan' : `${sub.tier?.label ?? 'Subscribed'}${price ? ` · ${price}` : ''}`;
    if (sub.cancelAtPeriodEnd && sub.pendingInterval) {
      body = `Moves to monthly billing on ${fmtDate(sub.currentPeriodEnd)}.`;
    } else if (sub.cancelAtPeriodEnd) {
      tone = 'warn';
      body = `Cancelled — you have full access until ${fmtDate(sub.currentPeriodEnd)}, then the account becomes read only.`;
    } else {
      body = `${sub.interval === 'year' ? 'Annual' : 'Monthly'} billing. Next payment ${fmtDate(sub.currentPeriodEnd) ?? 'at renewal'}.`;
    }
  } else {
    title = 'No active plan';
    body = 'Choose a plan below to keep using Clock-out.';
  }

  const tones = {
    ink:   'border-sand-200 bg-sand-50',
    trial: 'border-[color:var(--warn-border)] bg-[color:var(--warn-bg-soft)]',
    warn:  'border-[color:var(--warn-border)] bg-[color:var(--warn-bg-soft)]',
    bad:   'border-[color:var(--bad-border)] bg-[color:var(--bad-bg)]',
  };
  const Icon = tone === 'bad' ? AlertTriangle : tone === 'trial' ? Sparkles : CreditCard;

  return (
    <div className={cx('flex items-start gap-4 rounded-2xl border p-4 sm:p-5', tones[tone])}>
      <span className={cx(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
        tone === 'bad' ? 'bg-[color:var(--surface)] text-[color:var(--bad-text)]' : 'bg-[color:var(--surface)] text-ink',
      )}>
        <Icon size={20} />
      </span>
      <div className="min-w-0">
        <h3 className={cx('m-0 text-base font-semibold', tone === 'bad' ? 'text-[color:var(--bad-text)]' : 'text-sand-900')}>{title}</h3>
        <p className="m-0 mt-1 text-sm leading-relaxed text-sand-700">{body}</p>
        {sub?.bandChangesAtRenewal && sub.nextTier && (
          <p className="m-0 mt-2 text-[13px] text-sand-600">
            You now have {plural(summary.activeMembers, 'active person')}, so from your next renewal
            on {fmtDate(sub.currentPeriodEnd)} you'll move to <strong>{sub.nextTier.label}</strong> at {sub.nextTier.formatted} per {sub.interval === 'year' ? 'year' : 'month'}.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Price list ──────────────────────────────────────────────────────────────
function PriceTable({ priceList, interval, recommendedCode, activeMembers }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-sand-200">
      {priceList.tiers.map((t, i) => {
        const recommended = t.code === recommendedCode;
        const amount = interval === 'year' ? t.yearFormatted : t.monthFormatted;
        return (
          <div
            key={t.code}
            className={cx(
              'flex items-center justify-between gap-3 px-4 py-3 text-sm',
              i > 0 && 'border-t border-sand-100',
              recommended ? 'bg-[color:var(--tint)]' : 'bg-[color:var(--surface)]',
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className={cx('font-medium', recommended ? 'text-sand-900' : 'text-sand-700')}>{t.label}</span>
              {recommended && <Chip tone="ink"><Check size={10} /> Your size · {plural(activeMembers, 'person')}</Chip>}
            </span>
            <span className={cx('whitespace-nowrap', recommended ? 'font-semibold text-sand-900' : 'text-sand-700')}>
              {t.enterprise
                ? <a href={`mailto:${SALES_EMAIL}?subject=Clock-out%20Enterprise`} className="font-medium text-ink underline underline-offset-2">Talk to us</a>
                : <>{amount}<span className="text-sand-500"> / {interval === 'year' ? 'year' : 'month'}</span></>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main section ────────────────────────────────────────────────────────────
export default function BillingSection() {
  const { refreshBilling } = useApp();
  const confirm = useConfirm();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingInterval, setBillingInterval] = useState('month');
  const [code, setCode] = useState('');
  const [codeResult, setCodeResult] = useState(null);
  const [busy, setBusy] = useState(null); // 'checkout' | 'portal' | 'cancel' | 'resume' | 'interval' | 'country' | 'code' | 'sync'
  const [country, setCountry] = useState({ billingCountry: 'GB', region: '' });
  const [regions, setRegions] = useState([]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiService.getBillingSummary();
      const data = res?.data ?? res;
      setSummary(data);
      if (data?.subscription?.interval) setBillingInterval(data.subscription.interval);
      setCountry({ billingCountry: data?.billingCountry || 'GB', region: data?.region || '' });
    } catch (e) {
      setError(errorMessage(e, "Couldn't load billing."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Back from Stripe checkout: bring the page up to date straight away
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('checkout');
    const finish = () => {
      try { window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`); } catch { /* ignore */ }
    };
    if (result === 'success') {
      setBusy('sync');
      apiService.syncBilling(params.get('session_id'))
        .then(res => {
          setSummary(res?.data ?? res);
          toast.success("You're subscribed — thank you!");
        })
        .catch(() => load())
        .finally(() => { setBusy(null); setLoading(false); refreshBilling?.(); finish(); });
      return;
    }
    if (result === 'cancelled') {
      toast('Checkout cancelled — nothing has been charged.');
      finish();
    }
    load();
  }, [load, refreshBilling]);

  // States / territories for the chosen billing country
  useEffect(() => {
    let cancelled = false;
    apiService.getBillingPlans(country.billingCountry)
      .then(res => { if (!cancelled) setRegions(res?.data?.regions ?? []); })
      .catch(() => { if (!cancelled) setRegions([]); });
    return () => { cancelled = true; };
  }, [country.billingCountry]);

  const after = async (fn, key, message) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res?.data?.url) {
        window.location.assign(res.data.url);
        return;
      }
      if (res?.data && typeof res.data === 'object' && 'status' in res.data) setSummary(res.data);
      else await load();
      if (message || res?.message) toast.success(message || res.message);
      refreshBilling?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  const checkCode = async () => {
    const value = code.trim();
    if (!value) return;
    setBusy('code');
    try {
      const res = await apiService.validateBillingCode(value);
      setCodeResult(res?.data ?? null);
    } catch (e) {
      setCodeResult({ valid: false });
    } finally {
      setBusy(null);
    }
  };

  const recommendedCode = summary?.recommendedTier?.code;
  const recommendedTier = useMemo(
    () => summary?.priceList?.tiers?.find(t => t.code === recommendedCode) ?? null,
    [summary, recommendedCode],
  );

  if (loading && !summary) {
    return (
      <div className="space-y-4">
        <div className="h-16 animate-pulse rounded-xl bg-sand-50" />
        <div className="h-28 animate-pulse rounded-2xl bg-sand-50" />
        <SkeletonRows rows={4} height="h-12" />
      </div>
    );
  }
  if (error && !summary) {
    return (
      <Notice tone="error">
        {error} <button type="button" className="font-semibold underline" onClick={load}>Try again</button>
      </Notice>
    );
  }
  if (!summary) return null;

  const sub = summary.subscription;
  const subscribedLive = !!sub && summary.status !== 'canceled' && summary.status !== 'expired' && summary.status !== 'manual';
  const showPlans = summary.canCheckout || !subscribedLive;
  const taxNote = summary.tax?.inclusive ? `including ${summary.tax.name}` : `+ ${summary.tax?.name ?? 'tax'}`;
  const ctaPrice = recommendedTier
    ? (billingInterval === 'year' ? recommendedTier.yearFormatted : recommendedTier.monthFormatted)
    : null;
  const needsEnterprise = summary.requiresEnterprise && !summary.enterprise?.checkoutReady;

  return (
    <div>
      <SectionHeader
        title="Billing"
        description="Clock-out is priced by the number of active people in your company. Every feature is included at every size."
        action={(
          <Button size="sm" icon={RefreshCw} loading={busy === 'sync'} onClick={() => after(() => apiService.syncBilling(), 'sync')}>
            Refresh
          </Button>
        )}
      />

      <div className="space-y-6">
        <StatusCard summary={summary} />

        {!summary.billingConfigured && (
          <Notice tone="warn">Online payments aren't switched on for this server yet, so plans can't be bought here. Contact {SALES_EMAIL} to subscribe.</Notice>
        )}

        {/* At a glance */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { icon: Users, label: 'Active people', value: summary.activeMembers, hint: 'Archived and deactivated people aren\'t counted' },
            { icon: Tag, label: 'Your price band', value: summary.recommendedTier?.label ?? '—', hint: summary.requiresEnterprise ? 'Enterprise pricing' : 'Updates at each renewal' },
            { icon: Globe, label: 'Billing country', value: summary.marketName, hint: `Prices in ${summary.currency?.toUpperCase()} ${taxNote}` },
          ].map(s => (
            <div key={s.label} className="rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-sand-600"><s.icon size={14} /> {s.label}</div>
              <div className="mt-1.5 text-lg font-semibold text-sand-900">{s.value}</div>
              <div className="mt-0.5 text-xs text-sand-500">{s.hint}</div>
            </div>
          ))}
        </div>

        {/* Manage an existing subscription */}
        {subscribedLive && (
          <div className="rounded-2xl border border-sand-200 p-4 sm:p-5">
            <h3 className="m-0 text-sm font-semibold text-sand-900">Your subscription</h3>
            <p className="m-0 mt-1 text-[13px] text-sand-600">
              Update your card, download invoices and add your {summary.tax?.name ?? 'tax'} number in the billing portal.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="primary" icon={ExternalLink} loading={busy === 'portal'} disabled={!summary.canManage} onClick={() => after(() => apiService.openBillingPortal(), 'portal')}>
                Manage billing
              </Button>
              {!summary.enterprise && sub.interval === 'month' && !sub.cancelAtPeriodEnd && (
                <Button
                  icon={CalendarClock}
                  loading={busy === 'interval'}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Switch to annual billing?',
                      message: `You'll pay for a year now (two months free), less what's left of this month.`,
                      confirmLabel: 'Switch to annual',
                    });
                    if (ok) after(() => apiService.changeBillingInterval('year'), 'interval');
                  }}
                >
                  Switch to annual (2 months free)
                </Button>
              )}
              {!summary.enterprise && sub.interval === 'year' && !sub.cancelAtPeriodEnd && (
                <Button
                  icon={CalendarClock}
                  loading={busy === 'interval'}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Switch to monthly billing?',
                      message: `Your annual plan runs until ${fmtDate(sub.currentPeriodEnd)}, then monthly billing starts.`,
                      confirmLabel: 'Switch at renewal',
                    });
                    if (ok) after(() => apiService.changeBillingInterval('month'), 'interval');
                  }}
                >
                  Switch to monthly
                </Button>
              )}
              {sub.cancelAtPeriodEnd ? (
                <Button loading={busy === 'resume'} onClick={() => after(() => apiService.resumeSubscription(), 'resume')}>
                  {sub.pendingInterval ? 'Stay on annual billing' : 'Keep my subscription'}
                </Button>
              ) : (
                <Button
                  variant="dangerGhost"
                  loading={busy === 'cancel'}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Cancel your subscription?',
                      message: `You'll keep full access until ${fmtDate(sub.currentPeriodEnd)}. After that the account becomes read only — nothing is deleted, and you can resubscribe any time.`,
                      confirmLabel: 'Cancel at end of period',
                      tone: 'danger',
                    });
                    if (ok) after(() => apiService.cancelSubscription(), 'cancel');
                  }}
                >
                  Cancel subscription
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Plans + checkout */}
        {showPlans && summary.priceList && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="m-0 text-sm font-semibold text-sand-900">Choose a plan</h3>
                <p className="m-0 mt-0.5 text-[13px] text-sand-600">One price for the whole company. Prices {taxNote}.</p>
              </div>
              <Segmented
                value={billingInterval}
                onChange={setBillingInterval}
                options={[
                  { value: 'month', label: 'Monthly' },
                  { value: 'year', label: 'Annual · 2 months free' },
                ]}
              />
            </div>

            <PriceTable
              priceList={summary.priceList}
              interval={billingInterval}
              recommendedCode={recommendedCode}
              activeMembers={summary.activeMembers}
            />

            {needsEnterprise ? (
              <Notice tone="info">
                With more than 500 active people you're on Enterprise pricing. Email <a className="font-semibold underline" href={`mailto:${SALES_EMAIL}?subject=Clock-out%20Enterprise`}>{SALES_EMAIL}</a> and we'll set it up.
              </Notice>
            ) : (
              <div className="rounded-2xl border border-sand-200 p-4 sm:p-5">
                <label className="mb-1.5 block text-[13px] font-semibold text-sand-700" htmlFor="billing-code">Promo or partner code (optional)</label>
                <div className="flex flex-wrap gap-2">
                  <input
                    id="billing-code"
                    className={cx(inputClass, 'h-10 max-w-xs flex-1 uppercase')}
                    value={code}
                    onChange={e => { setCode(e.target.value); setCodeResult(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') checkCode(); }}
                    placeholder="e.g. LAUNCH20"
                    autoComplete="off"
                  />
                  <Button size="md" loading={busy === 'code'} disabled={!code.trim()} onClick={checkCode}>Apply</Button>
                </div>
                {codeResult && (
                  <p className={cx('m-0 mt-2 text-[13px]', codeResult.valid ? 'text-[color:var(--ok-text)]' : 'text-[color:var(--bad-text)]')}>
                    {codeResult.valid
                      ? codeResult.kind === 'partner'
                        ? `Partner code for ${codeResult.name}${codeResult.discount ? ` — ${codeResult.discount}` : ''}.`
                        : `Code applied — ${codeResult.discount ?? 'discount'} at checkout.`
                      : "That code isn't valid."}
                  </p>
                )}
                {summary.partner && !codeResult && (
                  <p className="m-0 mt-2 text-[13px] text-sand-600">Partner: {summary.partner.name ?? summary.partner.code}</p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    variant="primary"
                    icon={CreditCard}
                    loading={busy === 'checkout'}
                    disabled={!summary.canCheckout || (codeResult && !codeResult.valid)}
                    onClick={() => after(() => apiService.startCheckout({ interval: billingInterval, code: codeResult?.valid ? code.trim() : undefined }), 'checkout')}
                  >
                    {summary.enterprise?.checkoutReady
                      ? `Subscribe — ${summary.enterprise.formatted} per ${summary.enterprise.interval === 'year' ? 'year' : 'month'}`
                      : ctaPrice ? `Subscribe — ${ctaPrice} per ${billingInterval === 'year' ? 'year' : 'month'}` : 'Subscribe'}
                  </Button>
                  <span className="text-xs text-sand-500">
                    {summary.status === 'trialing' && (summary.trial?.daysLeft ?? 0) > 2
                      ? `First payment on ${fmtDate(summary.trial.endsAt)}, when your trial ends. `
                      : ''}
                    Secure payment by Stripe. Cancel any time.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Billing country */}
        <div className="rounded-2xl border border-sand-200 p-4 sm:p-5">
          <SettingRow
            label="Billing country"
            hint={summary.canChangeCountry
              ? 'Sets your currency, tax and local defaults. It can\'t be changed once you subscribe.'
              : `Billed in ${summary.currency?.toUpperCase()}. Contact us if your company moves country.`}
          >
            {summary.canChangeCountry ? (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className={cx(selectClass, 'w-56')}
                  value={country.billingCountry}
                  onChange={e => setCountry({ billingCountry: e.target.value, region: '' })}
                  aria-label="Billing country"
                >
                  {MARKET_OPTIONS.map(m => <option key={m.code} value={m.code}>{m.name}</option>)}
                </select>
                {regions.length > 0 && (
                  <select
                    className={cx(selectClass, 'w-56')}
                    value={country.region}
                    onChange={e => setCountry(c => ({ ...c, region: e.target.value }))}
                    aria-label={country.billingCountry === 'AU' ? 'State or territory' : 'Region'}
                  >
                    <option value="">{country.billingCountry === 'AU' ? 'Choose state or territory…' : 'Whole country'}</option>
                    {regions.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                  </select>
                )}
                <Button
                  loading={busy === 'country'}
                  disabled={
                    (country.billingCountry === summary.billingCountry && (country.region || '') === (summary.region || ''))
                    || (country.billingCountry === 'AU' && !country.region)
                  }
                  onClick={() => after(() => apiService.changeBillingCountry(country.billingCountry, country.region || null), 'country', 'Billing country updated')}
                >
                  Save
                </Button>
              </div>
            ) : (
              <span className="text-sm font-medium text-sand-900">{summary.marketName}</span>
            )}
          </SettingRow>
        </div>
      </div>
    </div>
  );
}